"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeOrderItemTotals = exports.getItemSavedAmount = void 0;
const domain_1 = require("@oolio-group/domain");
const computePrice_1 = require("./computePrice");
const utils_1 = require("./utils");
const getItemSavedAmount = (item) => {
    if (item.savedAmount && item.savedAmount > 0) {
        return item.quantity * item.savedAmount;
    }
    else
        return 0;
};
exports.getItemSavedAmount = getItemSavedAmount;
/**
 * Computes additional totals of an order item (based) on order.
 * @param item Order item
 * @param orderSubTotal Order sub total
 * @param orderDiscounts Array of order discount
 */
function computeOrderItemTotals(item, orderSubTotal, orderAdjustments, limitPrecision = true) {
    var _a;
    // converting flat to percentage based on "orderSubTotal" (if it not already).
    const orderAdjustmentsInPercentage = orderAdjustments === null || orderAdjustments === void 0 ? void 0 : orderAdjustments.map(adjustment => {
        const adjInPercentage = (0, computePrice_1.flatToPercentageForAdjustment)(adjustment, orderSubTotal);
        return (0, computePrice_1.limitAdjustmentToMaxAmount)(adjInPercentage, orderSubTotal);
    });
    const orderAdjustmentsOnSubTotal = orderAdjustmentsInPercentage === null || orderAdjustmentsInPercentage === void 0 ? void 0 : orderAdjustmentsInPercentage.filter(a => !a.doNotIncludeInSalesAmount);
    const orderAdjustmentsOnAdjustment = orderAdjustmentsInPercentage === null || orderAdjustmentsInPercentage === void 0 ? void 0 : orderAdjustmentsInPercentage.filter(a => a.doNotIncludeInSalesAmount);
    const grossPrice = (0, computePrice_1.computeLineTotal)(item);
    // converting flat to percentage (if it not already).
    const itemAdjustments = ((_a = item === null || item === void 0 ? void 0 : item.adjustments) === null || _a === void 0 ? void 0 : _a.map(x => (0, computePrice_1.flatToPercentageForAdjustment)(x, grossPrice))) ||
        [];
    const processedItemForTax = (0, computePrice_1.processOrderItemForTax)(item);
    const taxRecord = (0, computePrice_1.computeOrderItemTaxes)({
        ...processedItemForTax,
        adjustments: orderAdjustmentsOnSubTotal,
    });
    const taxes = Object.values(taxRecord);
    const totalTax = taxes.reduce((totalTaxAmount, prev) => totalTaxAmount + prev.amount, 0);
    const itemLevelTotal = (0, computePrice_1.computeOrderItemValue)({
        ...item,
        adjustments: itemAdjustments,
    }); // accounted item level adjustments
    // item level adjustments
    const itemLevelDiscount = (0, computePrice_1.computeOrderItemDiscount)({
        ...item,
        adjustments: itemAdjustments,
    });
    const itemLevelSurcharge = (0, computePrice_1.computeOrderItemSurcharge)({
        ...item,
        adjustments: itemAdjustments,
    });
    // order level adjustments
    const orderDiscountValueOnSubTotal = (0, utils_1.getAdjustmentValue)(itemLevelTotal, orderAdjustmentsOnSubTotal, { adjustmentType: domain_1.AdjustmentType.DISCOUNT });
    const orderRewardValueOnSubTotal = (0, utils_1.getAdjustmentValue)(itemLevelTotal, orderAdjustmentsOnSubTotal, { adjustmentType: domain_1.AdjustmentType.REWARD });
    const orderSurchargeValueOnSubTotal = (0, utils_1.getAdjustmentValue)(itemLevelTotal, orderAdjustmentsOnSubTotal, { adjustmentType: domain_1.AdjustmentType.SURCHARGE, escapePaymentType: true });
    const itemLevelTotalDue = (0, utils_1.sumDecimals)([
        Math.abs(itemLevelTotal),
        orderDiscountValueOnSubTotal,
        orderRewardValueOnSubTotal,
        orderSurchargeValueOnSubTotal,
    ]);
    const orderDiscountValueAfterAdjustment = (0, utils_1.getAdjustmentValue)(itemLevelTotalDue, orderAdjustmentsOnAdjustment, { adjustmentType: domain_1.AdjustmentType.DISCOUNT });
    const orderRewardValueAfterAdjustment = (0, utils_1.getAdjustmentValue)(itemLevelTotalDue, orderAdjustmentsOnAdjustment, { adjustmentType: domain_1.AdjustmentType.REWARD });
    const orderSurchargeValueAfterAdjustment = (0, utils_1.getAdjustmentValue)(itemLevelTotalDue, orderAdjustmentsOnAdjustment, { adjustmentType: domain_1.AdjustmentType.SURCHARGE, escapePaymentType: true });
    const otherCharges = (0, utils_1.sumDecimals)([
        orderRewardValueAfterAdjustment,
        orderSurchargeValueAfterAdjustment,
        orderDiscountValueAfterAdjustment,
    ]);
    // calculate final results
    const totalDiscount = (0, utils_1.sumDecimals)([
        Math.abs(orderDiscountValueOnSubTotal),
        Math.abs(orderRewardValueOnSubTotal),
        itemLevelDiscount,
    ]);
    const totalSurcharge = (0, utils_1.sumDecimals)([
        orderSurchargeValueOnSubTotal,
        itemLevelSurcharge,
    ]);
    const subTotal = (0, utils_1.sumDecimals)([grossPrice, -totalDiscount, totalSurcharge]);
    //basically returning subtotal with either netAmount or totalPrice
    const netAmount = (0, utils_1.sumDecimals)([
        subTotal,
        -(item.taxInclusive ? totalTax : 0),
    ]);
    const totalPrice = (0, utils_1.sumDecimals)([netAmount, totalTax]);
    if (limitPrecision) {
        const limitedGross = (0, utils_1.limitDecimalCount)(grossPrice);
        const limitedDiscount = (0, utils_1.limitDecimalCount)(totalDiscount);
        const limitedSurcharge = (0, utils_1.limitDecimalCount)(totalSurcharge);
        const limitedTax = (0, utils_1.limitDecimalCount)(taxes.reduce((totalTaxAmount, prev) => (0, utils_1.sumDecimals)([totalTaxAmount, (0, utils_1.limitDecimalCount)(prev.amount)]), 0));
        const limitedSubtotal = (0, utils_1.limitDecimalCount)((0, utils_1.sumDecimals)([limitedGross, -limitedDiscount, limitedSurcharge]));
        const limitedNet = (0, utils_1.sumDecimals)([
            limitedSubtotal,
            -(item.taxInclusive ? limitedTax : 0),
        ]);
        return {
            taxes: taxes.map(entry => ({
                ...entry,
                amount: (0, utils_1.limitDecimalCount)(entry.amount),
            })),
            grossPrice: limitedGross,
            subTotal: limitedSubtotal,
            netAmount: limitedNet,
            totalDiscount: limitedDiscount,
            totalSurcharge: limitedSurcharge,
            totalTax: limitedTax,
            totalPrice: (0, utils_1.sumDecimals)([limitedTax, limitedNet]),
            otherCharges,
        };
    }
    return {
        taxes,
        grossPrice,
        subTotal,
        netAmount,
        totalDiscount,
        totalSurcharge,
        totalTax,
        totalPrice,
        otherCharges,
    };
}
exports.computeOrderItemTotals = computeOrderItemTotals;
//# sourceMappingURL=computeOrderItemTotals.js.map